import { BleClient, ScanResult } from '@capacitor-community/bluetooth-le';
import { SLink } from 'foreman-slink';
import { bleActions } from 'src/redux/ble/actions';
import { BleConnectionState } from 'src/redux/ble/constants';
import { selectBleConnectionState, selectBleIsLocked } from 'src/redux/ble/selectors';
import { BleControllerDevice } from 'src/redux/ble/types';
import { store } from 'src/redux/store';
import { BleControllerService } from './BleControllerService';
import { IBleControllerService } from './IBleControllerService';
import { LiveJobLogService } from './LiveJobLogService';
import { bleIds } from './constants';

export class BleService {
  public static controller?: IBleControllerService;
  private static readonly _jobLogServicesByFilename = new Map<string, LiveJobLogService>();
  private static readonly _quickReconnectKey = 'BleService.QuickReconnect';
  private static readonly _didInitializeKey = 'BleService.DidInitialize';

  /**
   * Keeps track of whether the application is using the BLE device.
   * Only one interaction with the BLE device is allowed at a time.
   */
  private static get lock() {
    return selectBleIsLocked(store.getState());
  }

  public static get jobLogServicesByFilename() {
    return this._jobLogServicesByFilename;
  }

  private static get quickReconnectDevice(): BleControllerDevice | undefined {
    const result = window.localStorage.getItem(this._quickReconnectKey);
    if (!result) return undefined;
    try {
      return JSON.parse(result);
    } catch {
      return undefined;
    }
  }

  private static set quickReconnectDevice(data: BleControllerDevice | undefined) {
    if (!data) window.localStorage.removeItem(this._quickReconnectKey);
    else window.localStorage.setItem(this._quickReconnectKey, JSON.stringify(data));
  }

  private static get didInitialize(): boolean {
    return !!window.sessionStorage.getItem(this._didInitializeKey);
  }

  private static set didInitialize(value: boolean) {
    if (value) window.sessionStorage.setItem(this._didInitializeKey, 'true');
    else window.sessionStorage.removeItem(this._didInitializeKey);
  }

  /**
   * Scans for available controller devices.
   * The results may be read out of the redux store.
   * @param signal An abort signal allowing the caller to cancel the scan.
   */
  public static readonly scan = async (signal?: AbortSignal) => {
    if (this.lock) {
      console.warn(
        'Tried to scan for BLE devices while another process held the lock. This likely represents a timing issue in the consuming code.',
      );
      return;
    }
    try {
      store.dispatch(bleActions.scanDevicesStart());

      await this.ensureInitialized();
      await BleClient.requestLEScan({ namePrefix: 'SA_' }, this.onScanResult);

      const until = Date.now() + 5000;
      while (Date.now() < until) {
        await new Promise(res => window.setTimeout(res, 125));
        if (signal?.aborted) break;
      }

      await BleClient.stopLEScan();
      store.dispatch(bleActions.scanDevicesSuccess());
    } catch (error) {
      console.error('Failed to scan for devices:', error);
      store.dispatch(bleActions.scanDevicesFailure({ error: error instanceof Error ? error.message : undefined }));
    }
  };

  /**
   * Connects to the provided controller device.
   * @param device The controller device to connect.
   * @param onDisconnect Called when the controller is disconnected.
   * @throws If the device fails to connect.
   */
  public static readonly connect = async (device: BleControllerDevice, onDisconnect?: () => void): Promise<void> => {
    let ctrl: IBleControllerService | undefined;
    if (this.lock) {
      throw new Error(
        'Tried to connect to BLE device while another process held the lock. This likely represents a timing issue in the consuming code.',
      );
    }
    try {
      store.dispatch(bleActions.connectDeviceStart());

      await this.ensureInitialized();
      ctrl = new BleControllerService(device, this.getJobLogService, this.addJobLogService);

      let didDisconnect = true;
      const handleDisconnect = () => {
        if (didDisconnect) return;
        didDisconnect = true;
        console.log('[BleService] Device disconnected', device.deviceId);
        ctrl?.close();
        if (ctrl !== this.controller) this.controller?.close();
        onDisconnect?.();
        this.quickReconnectDevice = undefined;
        this.controller = undefined;
        store.dispatch(bleActions.disconnectDevice({}));
      };
      await BleClient.connect(device.deviceId, handleDisconnect);

      const controller = await ctrl.initialize();
      this.quickReconnectDevice = device;
      this.controller = ctrl;
      didDisconnect = false;
      store.dispatch(bleActions.connectDeviceSuccess({ device: controller }));
    } catch (e) {
      console.error(e);
      try {
        await ctrl?.close();
      } catch {}
      store.dispatch(bleActions.connectDeviceFailure({ error: e instanceof Error ? e.message : undefined }));
      throw e;
    }
  };

  /**
   * In the event that the app state indicates we are not connected to a device, but the BLE service indicates we are connected to a device,
   * reconcile the two by automatically reconnecting to that device and getting the app state in sync.
   */
  public static readonly quickReconnect = async () => {
    try {
      const target = this.quickReconnectDevice;
      if (!target) return;

      const appConnectionState = selectBleConnectionState(store.getState());
      if (appConnectionState === BleConnectionState.CONNECTED) return; // Only attempt quick reconnect if the redux state thinks we're not currently connected.

      await this.ensureInitialized();
      const connectedDevices = await BleClient.getConnectedDevices([bleIds.uartService]);
      if (!connectedDevices.some(d => d.deviceId === target.deviceId)) return;
      console.log('[BleService] Performing quick reconnect for device:', target);

      await BleClient.disconnect(target.deviceId);
      await this.connect(target);
    } catch {}
  };

  /**
   * Schedules the live job log for the provided filename to be closed.
   * @param filename The filename of the log to close.
   */
  public static readonly closeLog = (filename: string) => {
    const svc = this._jobLogServicesByFilename.get(filename);
    if (!svc) throw new Error(`No live job log service for the provided filename: ${filename}`);
    svc.close();
  };

  public static readonly getJobLogService = (filename: string) => {
    return this._jobLogServicesByFilename.get(filename);
  };

  public static readonly addJobLogService = (
    filename: string,
    serialNumber: string,
    skipMetadataCollection?: boolean,
  ) => {
    if (this._jobLogServicesByFilename.has(filename))
      throw new Error(`Live job log service for file already exists: ${filename}`);
    this._jobLogServicesByFilename.set(
      filename,
      new LiveJobLogService(filename, serialNumber, skipMetadataCollection ?? false),
    );
    return this._jobLogServicesByFilename.get(filename)!;
  };

  public static readonly disconnect = async (deviceId: string) => {
    try {
      await BleClient.disconnect(deviceId);
      store.dispatch(bleActions.disconnectDevice({}));
      this.controller = undefined; // Set controller to undefined after disconnecting
    } catch (e) {
      console.error('Failed to disconnect device', e);
    }
  };

  private static readonly ensureInitialized = async () => {
    if (this.didInitialize) return;
    await BleClient.initialize({ androidNeverForLocation: true });
    await BleClient.startEnabledNotifications(isEnabled => {
      // TODO: Show indicator somewhere for bluetooth enabled/disabled
    });
    this.didInitialize = true;
  };

  /** Called when a device is found while scanning. */
  private static readonly onScanResult = async (result: ScanResult) => {
    try {
      const slink = new SLink(async (data: Uint8Array) => {
        await BleClient.writeWithoutResponse(
          result.device.deviceId,
          bleIds.uartService,
          bleIds.tx,
          new DataView(data.buffer),
        );
      });
      await BleClient.connect(result.device.deviceId, () => {
        slink.removeAllListeners();
      });
      await BleClient.startNotifications(result.device.deviceId, bleIds.uartService, bleIds.rx, (data: DataView) => {
        const uint8View = new Uint8Array(data.buffer);
        slink.parse(uint8View);
      });

      const serial = await slink.getSerialNumber(2000);
      const firmware = await slink.getFWVersion(2000);
      if (typeof serial !== 'string')
        throw new Error(`Could not read serial number for device ${result.device.deviceId}`);
      if (typeof firmware !== 'string')
        throw new Error(`Could not read firmware version for device ${result.device.deviceId}`);

      store.dispatch(
        bleActions.scanDevicesProgress({
          device: {
            serialNumber: serial,
            firmwareVersion: firmware,
            deviceId: result.device.deviceId,
            name: result.device.name,
          },
        }),
      );
    } catch (e) {
      console.error('Failed to connect to device during scan:', e);
    } finally {
      try {
        await BleClient.stopNotifications(result.device.deviceId, bleIds.uartService, bleIds.rx);
      } catch {}
      try {
        await BleClient.disconnect(result.device.deviceId);
      } catch {}
    }
  };
}

if (module.hot) {
  // transfer instances of services from old module to new
  module.hot.dispose(data => {
    data.logServices = BleService.jobLogServicesByFilename;
    data.controller = BleService.controller;
  });

  if (module.hot.data?.logServices) {
    BleService.controller = module.hot.data.controller;
    if (BleService.controller) {
      BleService.controller.addJobLogService = BleService.addJobLogService;
      BleService.controller.getJobLogService = BleService.getJobLogService;
    }

    for (const [key, value] of module.hot.data.logServices) {
      BleService.jobLogServicesByFilename.set(key, value);
    }
  }
}

BleService.quickReconnect(); // Quick reconnect on startup
